import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function MailLink({ email, phone, testid }) {
  if (!email) return <span className="text-slate-300" data-testid={testid}>call Brandon Rinker Jr. at {phone || "the program number"} or reply HELP to any text from Brandon Rinker Jr.</span>;
  return <span data-testid={testid}>email <a href={`mailto:${email}`} className="text-orange-400 hover:text-orange-300 underline">{email}</a></span>;
}

export default function Privacy() {
  const [contacts, setContacts] = useState({ privacy_email: null, support_email: null, help_phone: null });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/legal-contacts`).then((r) => setContacts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <nav className="border-b border-slate-800 bg-[#0B0F17]/85 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/" className="text-sm text-slate-400 hover:text-white" data-testid="privacy-nav-home">← Back to home</Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-16" data-testid="privacy-page">
        <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-3">Privacy</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mb-3">Privacy Policy</h1>
        <p className="text-slate-500 text-sm mb-10" data-testid="privacy-updated">Last updated: June 2026</p>

        <div className="space-y-6 text-slate-300 leading-relaxed">
          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">1. Who we are</h2>
            <p>
              RoofLead ("RoofLead", "we", "us", "our") is a software and service platform that helps roofing businesses
              respond to incoming leads and manage customer conversations by phone and text. RoofLead is <span className="text-white font-semibold">not</span> a
              roofing contractor and does not perform roof inspections, roofing work, or any other construction services.
              All on-site services are performed by independent roofing businesses that use the RoofLead platform.
            </p>
            <p className="mt-3 bg-slate-800/60 border border-slate-700 rounded-lg p-4" data-testid="privacy-testing-program">
              <span className="text-white font-semibold">RoofLead platform development &amp; testing program.</span> Text
              messages and the consent phone line for this program are sent and operated by{" "}
              <span className="text-white font-semibold" data-testid="privacy-sms-sender">Brandon Rinker Jr.</span>, the registered
              SMS sender, as part of the RoofLead software development and testing program. RoofLead is only the name of the
              software product/platform. No roofing services, inspections, estimates, or roofing work are offered.
            </p>
            <p className="mt-3">
              This Privacy Policy explains what information we collect when you contact us or opt in to receive text messages,
              how we use it, and the choices you have.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">2. Information we collect</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><span className="text-white font-semibold">Contact information</span> you submit — name, mobile phone number, and the inquiry/message you write</li>
              <li><span className="text-white font-semibold">SMS opt-in / consent records</span> — for phone (IVR) opt-ins: your phone number, the number you called, the company/Brand, timestamp, consent script version, the key you pressed (1 = consent), the call ID, and the exact script you heard (see <Link to="/sms-consent" className="text-orange-400 hover:text-orange-300 underline">/sms-consent</Link>); for web opt-ins: whether you checked the SMS opt-in box, the exact disclosure text, timestamp, form URL, IP address, and user-agent</li>
              <li><span className="text-white font-semibold">Voicemail recordings</span> you choose to leave if you decline text messages</li>
              <li><span className="text-white font-semibold">Communication content</span> — the text messages and call transcripts exchanged between you and us (or a roofing business operating on the RoofLead platform)</li>
              <li><span className="text-white font-semibold">Technical data</span> — browser type, device type, referring page, and pages viewed on our site</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">3. How we use your information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To respond to your inquiry and operate the RoofLead platform development &amp; testing program</li>
              <li>To send SMS messages following up on your inquiry as part of that program — <span className="text-white font-semibold">only</span> if you have expressly opted in</li>
              <li>To operate, secure, and improve RoofLead</li>
              <li>To comply with legal obligations, including SMS carrier compliance (CTIA, A2P 10DLC) record-keeping of consent</li>
            </ul>
          </section>

          <section id="sms">
            <h2 className="font-display text-2xl font-bold text-white mb-3">4. SMS messaging program</h2>
            <p>
              <span className="text-white font-semibold">Brandon Rinker Jr.</span> sends the text messages for the{" "}
              <span className="text-white font-semibold">RoofLead software development &amp; testing program</span>; RoofLead is the software platform used to send them.
              We will only send you SMS messages if you have provided express consent, either by pressing 1 on your phone
              keypad after hearing the Brandon Rinker Jr. consent message when your call is missed (see{" "}
              <Link to="/sms-consent" className="text-orange-400 hover:text-orange-300 underline">/sms-consent</Link>), or by checking the
              un-prechecked opt-in checkbox on the web form at <Link to="/get-quote" className="text-orange-400 hover:text-orange-300 underline">/get-quote</Link>.
              If you do not press 1, no text message is sent.
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-3">
              <li><span className="text-white font-semibold">Sender:</span> Brandon Rinker Jr.</li>
              <li><span className="text-white font-semibold">Program description:</span> RoofLead software development &amp; testing program — text messages from Brandon Rinker Jr. following up on your call or inquiry. RoofLead is software and does not provide roofing services. We do not send marketing or promotional SMS under this program.</li>
              <li><span className="text-white font-semibold">Message frequency:</span> varies based on your inquiry activity.</li>
              <li><span className="text-white font-semibold">Cost:</span> message and data rates may apply from your mobile carrier.</li>
              <li><span className="text-white font-semibold">Opt out at any time</span> by replying <span className="font-mono text-orange-400">STOP</span> to any text you receive from Brandon Rinker Jr.</li>
              <li><span className="text-white font-semibold">Help:</span> reply <span className="font-mono text-orange-400">HELP</span> to any text, or <MailLink phone={contacts.help_phone} email={contacts.privacy_email} testid="privacy-email-link" />.</li>
              <li><span className="text-white font-semibold">Consent is not a condition</span> of submitting an inquiry or purchasing any service.</li>
              <li><span className="text-white font-semibold">Carriers are not liable</span> for delayed or undelivered messages.</li>
            </ul>
          </section>

          <section id="sharing">
            <h2 className="font-display text-2xl font-bold text-white mb-3">5. We do not sell or share your SMS data</h2>
            <p className="bg-slate-800/60 border border-slate-700 rounded-lg p-4" data-testid="privacy-no-share-clause">
              <span className="text-white font-semibold">
                Brandon Rinker Jr. will not sell or share your mobile phone number, SMS opt-in information, or SMS consent records
                with third parties or affiliates for marketing or promotional purposes.
              </span>{" "}
              The limited sharing described below is strictly for operating the service you asked for.
            </p>
            <p className="mt-3">We share limited information only with the following service providers, strictly to deliver the service you requested:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><span className="text-white font-semibold">Twilio, Inc.</span> — to deliver SMS and voice messages that you have consented to receive</li>
              <li><span className="text-white font-semibold">Anthropic (Claude)</span> — to help draft reply suggestions from the content of your messages. We do not transmit your phone number to Anthropic.</li>
              <li><span className="text-white font-semibold">Cloud hosting providers</span> — to store your data securely at rest</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">6. Your rights</h2>
            <p>
              Depending on your jurisdiction, you may have the right to access, correct, delete, or export the personal
              information we hold about you, and to withdraw consent at any time. To exercise any of these rights,
              <MailLink phone={contacts.help_phone} email={contacts.privacy_email} testid="privacy-rights-email" />.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">7. Data retention</h2>
            <p>
              We retain contact information and SMS opt-in records for as long as reasonably necessary to deliver the
              service you requested and to comply with applicable legal obligations, including SMS carrier compliance
              (CTIA, A2P 10DLC) proof-of-consent record-keeping requirements. We review retained records periodically
              and delete records that are no longer needed for either purpose.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">8. Changes to this policy</h2>
            <p>
              We will post any changes on this page and update the "Last updated" date above. Continued use of our
              services after a change constitutes acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">9. Contact us</h2>
            <p>
              Questions about this policy? You can <MailLink phone={contacts.help_phone} email={contacts.privacy_email} testid="privacy-contact-email" />.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-wrap gap-4 text-sm">
          <Link to="/terms" className="text-orange-400 hover:text-orange-300" data-testid="privacy-terms-link">Terms of Service →</Link>
          <Link to="/get-quote" className="text-orange-400 hover:text-orange-300" data-testid="privacy-quote-link">Contact RoofLead →</Link>
        </div>
      </article>
    </div>
  );
}
